import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { ResponsiveDataList } from "../../components/responsive";
import Helpers from "../../components/Helpers";
import { utils, writeFile } from "xlsx";
import ActivityLogApi from "../../api/activityLogApi";
import {
  actionBadgeClass,
  activitySummaryText,
  ChangedDataView,
  displayActor,
  displayRole,
  eventLabel,
  ModuleLinkCell,
} from "../../utils/activityLogFormat";
import { LogReportToolbar } from "./logReport/manage/LogReportToolbar";
import { LogReportCard } from "./logReport/manage/cards/LogReportCard";

const LogReport = () => {
  const [LoadData, setLoadData] = useState(false);
  const [reportList, setReportList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [searchKey, setSearchKey] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const api = new ActivityLogApi();

  const getAllLogReport = useCallback(
    async (
      pageNo: any = 1,
      size: any = 20,
      searchKeyword: any = null,
      sortBy: any = {},
    ) => {
      setLoadData(true);
      const d: any = {
        pageNo,
        size,
        searchKeyword: searchKeyword !== null ? searchKeyword : searchKey,
        module: moduleFilter || undefined,
        action: actionFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        export: false,
      };
      if (sortBy?.sortColumn) d.sortBy = sortBy;
      try {
        const res: any = await api.list(d);
        if (res.success) {
          setReportList(res.data || []);
          setTotal(res.totalRecords || 0);
        } else {
          toast.error(res.msg || "Failed to load activity log");
        }
      } catch (error: any) {
        toast.error(error?.toString?.() || "Failed to load activity log");
      } finally {
        setLoadData(false);
      }
    },
    [searchKey, moduleFilter, actionFilter, dateFrom, dateTo],
  );

  useEffect(() => {
    getAllLogReport();
  }, [moduleFilter, actionFilter, dateFrom, dateTo]);

  const SearchData = (event: any) => {
    const key = event.target.value;
    setSearchKey(key);
    getAllLogReport(1, 20, key);
  };

  const columns = [
    {
      id: 1,
      name: "Time",
      selector: (row: any) => row.createdTime,
      cell: (row: any) =>
        Helpers.convertUtcToTimezone(row.createdTime, "MM-DD-YYYY hh:mm a"),
      sortable: true,
      width: "170px",
    },
    {
      id: 2,
      name: "User",
      selector: (row: any) => displayActor(row),
      cell: (row: any) => displayActor(row),
      sortable: true,
      width: "160px",
    },
    {
      id: 3,
      name: "Role",
      selector: (row: any) => displayRole(row),
      cell: (row: any) => displayRole(row),
      sortable: true,
      width: "110px",
    },
    {
      id: 4,
      name: "Action",
      selector: (row: any) => row.action,
      cell: (row: any) => (
        <span className={`badge ${actionBadgeClass(row.action)}`}>
          {row.action || "—"}
        </span>
      ),
      sortable: true,
      width: "110px",
    },
    {
      id: 5,
      name: "Module",
      selector: (row: any) => row.module || row.moduleLink,
      cell: (row: any) => <ModuleLinkCell row={row} />,
      sortable: true,
      width: "140px",
    },
    {
      id: 6,
      name: "Event",
      selector: (row: any) => row.eventType,
      cell: (row: any) => eventLabel(row.eventType) || "—",
      sortable: true,
      width: "190px",
    },
    {
      id: 7,
      name: "Changed data",
      selector: (row: any) =>
        row.changedDataParsed?.summary || row.changedData || "",
      cell: (row: any) => (
        <ChangedDataView raw={row.changedData} parsed={row.changedDataParsed} />
      ),
      grow: 2,
      wrap: true,
      width: "600px",
    },
  ];

  const handleExport = async () => {
    try {
      const res: any = await api.list({
        searchKeyword: searchKey,
        module: moduleFilter || undefined,
        action: actionFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        export: true,
      });
      if (!res.success) {
        toast.error(res.msg || "Export failed");
        return;
      }
      const exportData = (res.data || []).map((element: any) => ({
        Time: Helpers.convertUtcToTimezone(
          element.createdTime,
          "MM-DD-YYYY hh:mm a",
        ),
        User: displayActor(element),
        Role: displayRole(element),
        Action: element.action || "",
        Module: element.module || "",
        Event: eventLabel(element.eventType),
        Summary: activitySummaryText(
          element.changedData,
          element.changedDataParsed,
        ),
      }));
      const headings = [
        ["Time", "User", "Role", "Action", "Module", "Event", "Summary"],
      ];
      const wb = utils.book_new();
      const ws = utils.json_to_sheet([]);
      utils.sheet_add_aoa(ws, headings);
      utils.sheet_add_json(ws, exportData, { origin: "A2", skipHeader: true });
      utils.book_append_sheet(wb, ws, "Data");
      writeFile(wb, `Log Report.xlsx`);
    } catch (error: any) {
      toast.error(error?.toString?.() || "Export failed");
    }
  };

  return (
    <div className="row">
      <div className="col-lg-12 d-flex flex-column">
        <div className="card custom-card">
          <div className="card-body">
            <LogReportToolbar
              moduleFilter={moduleFilter}
              actionFilter={actionFilter}
              dateFrom={dateFrom}
              dateTo={dateTo}
              searchKey={searchKey}
              onModuleChange={setModuleFilter}
              onActionChange={setActionFilter}
              onDateFromChange={setDateFrom}
              onDateToChange={setDateTo}
              onSearch={SearchData}
              onExport={handleExport}
            />
            <ResponsiveDataList
              columns={columns}
              data={reportList}
              loading={LoadData}
              total={total}
              countPerPage={20}
              isServerSide
              sortServer
              pagination
              dataFetchApi={(pageNo, size, _search, sortBy) =>
                getAllLogReport(pageNo, size, searchKey || null, sortBy || {})
              }
              highlightOnHover
              pointerOnHover
              cardGridColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
              getRowId={(row: any) =>
                row?.id ?? `${row?.createdTime}-${row?.eventType}-${row?.userId}`
              }
              renderCard={(row) => <LogReportCard row={row} />}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogReport;
